import React, { useCallback, useMemo } from "react";
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import { debounce } from "lodash";
import { useEffect, useState } from "react";
import http from "../../utils/http";
import { errorToastMessage, toastMessage } from "../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../utils/dataTableShell";
import { MoreVert, History, CloseRounded, Add } from "@mui/icons-material";
import { AxiosResponse } from "axios";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import AddEConsentModal from "./AddEConsentModal";
import { DateTime } from "luxon";
import StatusBadge from "../Common/UI/StatusTag";
import AddTemplateModal from "./Builder/AddTemplateModal";
import EConsentVersionHistoryModal from "./EConsentVersionHistoryModal";
import { v4 as uuid } from "uuid";

type Props = {
  canEdit: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  searchId: string;
  setSearchId: React.Dispatch<React.SetStateAction<string>>;
  type: "platform" | "study";
  formType: "consent" | "sunshine";
};

const statusMap: any = {
  draft: { label: "Draft", variant: "completed" },
  active: { label: "Active", variant: "active" },
  inactive: { label: "Inactive", variant: "cancelled" },
};

function getFileNameFromUrl(fileUrl: string) {
  if (!fileUrl) return "";
  const parts = fileUrl.split("/");
  return parts[parts.length - 1];
}

const EConsentList = ({
  page,
  setPage,
  searchText,
  setSearchText,
  canEdit,
  type,
  searchId,
  setSearchId,
  formType,
}: Props) => {
  const { id: studyId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [dataCount, setDataCount] = useState(0);

  const [loading, setLoading] = useState<boolean>(true);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [previewMode, setPreviewMode] = useState<boolean>(false);
  const [menuLoader, setMenuLoader] = useState<string>("");
  const [showTemplate, setTemplateModal] = useState<boolean>(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState<any>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const prefix =
          type === "platform"
            ? `/platform/admin/econsent`
            : formType === "sunshine"
              ? `/admin/study/${studyId}/sunshine`
              : `/admin/study/${studyId}/econsent`;
        let url = `${prefix}?page=${page + 1}&size=${pageSize}`;
        if (searchText) {
          url += `&q=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.data;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          consentId: item?.id,
          consentName: item?.name,
          consentSummary: item?.description,
          version: item?.latestVersion?.version,
          versionId: item?.latestVersion?.id,
          startAndEndDate: item?.latestVersion?.startDate
            ? item?.latestVersion?.endDate
              ? `${DateTime.fromISO(item.latestVersion.startDate).toFormat(
                "MM/dd/yyyy"
              )} - ${DateTime.fromISO(item.latestVersion.endDate).toFormat(
                "MM/dd/yyyy"
              )}`
              : DateTime.fromISO(item.latestVersion.startDate).toFormat(
                "MM/dd/yyyy"
              )
            : "-",
          status: item?.latestVersion?.status,
          fileUrl: item?.fileUrl,
          slug: item?.slug,
          fileName: getFileNameFromUrl(item?.fileUrl),
          contentType: "builder",
        }));

        setData(newData || []);
        setDataCount(res.data?.data?.meta?.paginationOptions?.totalItems ?? 0);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [studyId, searchText, page, toggleLoader, type, formType]);

  const openModal = () => {
    setShowModal(true);
    setAnchorEl(null);
  };

  const openTemplateModal = () => {
    setTemplateModal(true);
    setAnchorEl(null);
  };

  const handleActionClick = useCallback(
    async (action: string) => {
      try {
        const prefix =
          type === "platform"
            ? `/platform/admin/econsent`
            : formType === "sunshine"
              ? `/admin/study/${studyId}/sunshine`
              : `/admin/study/${studyId}/econsent`;
        let url: string = `${prefix}/${selectedRow?.id}/versions/${selectedRow?.versionId}`;
        let body: any = null;
        let res: AxiosResponse;
        setMenuLoader(action);

        if (action === "delete") {
          res = await http.delete(url);
        } else {
          url += `/status`;
          body = {
            status:
              selectedRow?.status === "draft"
                ? "active"
                : selectedRow?.status === "active"
                  ? "inactive"
                  : "active",
          };
          res = await http.post(url, body);
        }
        handleMenuClose();
        toastMessage("success", res?.data?.message);
        setMenuLoader("");
        refreshPage();
      } catch (err) {
        errorToastMessage(err as Error);
        setMenuLoader("");
      }
    },
    [studyId, selectedRow, setMenuLoader, type, formType]
  );

  const closeTemplateModal = () => {
    setTemplateModal(false);
    setSelectedRow(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
    setPreviewMode(false);
  };

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleChangePage = (_1: any, newPage: number) => {
    setPage(newPage);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setPage(0);
        setSearchText(val);
      }, 500),
    [setPage, setSearchText]
  );

  const handlePreview = (row: any) => {
    if (row?.contentType === "builder") {
      if (type === "platform") {
        navigate(`/app/consent/${row.id}/edit?versionId=${row?.versionId}`);
      } else {
        navigate(
          `/studies/${studyId}/e-consent/form/${row?.id}/edit?versionId=${row?.versionId}&formType=${formType}`
        );
      }
    } else {
      setPreviewMode(true);
      setSelectedRow(row);
      openModal();
    }
  };

  const handleVersionHistory = async (consent: any) => {
    setSelectedConsent({
      ...consent,
    });
    setShowVersionModal(true);
  };

  const closeVersionModal = () => {
    setShowVersionModal(false);
    setSelectedConsent(null);
  };

  const handleAddNewVersion = async () => {
    try {
      setMenuLoader("addVersion");
      const body = {
        contentType: "builder",
        fileUrl: "",
      };
      const prefix =
        type === "platform"
          ? `/platform/admin/econsent`
          : formType === "sunshine"
            ? `/admin/study/${studyId}/sunshine`
            : `/admin/study/${studyId}/econsent`;
      let res = await http.post(`${prefix}/${selectedRow?.id}/versions`, body);
      toastMessage("success", res?.data?.message);
      handleMenuClose();
      refreshPage();
      setMenuLoader("");
    } catch (err) {
      setMenuLoader("");
      errorToastMessage(err as Error);
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    setPage(0);
    setSearchId(uuid());
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        toolbar={
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <TextField
              key={searchId}
              placeholder={`Search for ${formType === "consent" ? "Consent" : "Sunshine Forms"
                }`}
              style={{ width: "30%" }}
              defaultValue={searchText}
              onChange={(e) => modifySearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchText && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch}>
                      <CloseRounded />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowModal(true)}
              >
                Add {formType === "consent" ? "Consent" : "Sunshine Form"}
              </Button>
            )}
          </Box>
        }
        footer={
          <DataTableFooter
            filteredCount={dataCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handleChangePage}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ minWidth: 140 }}>
                {formType === "consent" ? "Consent Name" : "Form Name"}
              </StyledTableCell>
              <StyledTableCell>Version</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 200 }}>
                Start Date / End Date
              </StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              {canEdit && <StyledTableCell />}
            </TableRow>
          </TableHead>
          {!loading && data?.length > 0 && (
            <TableBody>
              {data?.map((row: any) => (
                <TableRow key={row?.id}>
                  <StyledTableCell
                    onClick={() => handlePreview(row)}
                    sx={{ cursor: "pointer" }}
                  >
                    {row?.consentName || "-"}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "regular",
                          color: "text.primary",
                        }}
                      >
                        {row?.version || "-"}
                      </Typography>
                      <IconButton
                        onClick={() => handleVersionHistory(row)}
                        sx={{
                          color: "primary.main",
                          padding: "4px",
                        }}
                      >
                        <History fontSize="small" />
                      </IconButton>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>{row?.startAndEndDate || "-"}</StyledTableCell>
                  <StyledTableCell>
                    <StatusBadge
                      label={statusMap[row?.status]?.label ?? row?.status}
                      variant={statusMap[row?.status]?.variant}
                    />
                  </StyledTableCell>

                  {canEdit && (
                    <StyledTableCell align="right">
                      <IconButton onClick={(e) => handleMenuClick(e, row)}>
                        <MoreVert />
                      </IconButton>
                    </StyledTableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!loading && data.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: "400px",
            width: "20ch",
          },
        }}
        TransitionProps={{
          exit: false,
        }}
      >
        {selectedRow?.status === "draft" && (
          <MenuItem onClick={openModal} disabled={!!menuLoader}>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {selectedRow?.status !== "draft" && (
          <MenuItem onClick={handleAddNewVersion} disabled={!!menuLoader}>
            <ListItemText>Add new version</ListItemText>
            {menuLoader === "addVersion" && (
              <ListItemIcon>
                <CircularProgress size={18} sx={{ ml: 1 }} />
              </ListItemIcon>
            )}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleActionClick("activeInactive")}
          disabled={!!menuLoader}
        >
          <ListItemText>
            {selectedRow?.status === "draft"
              ? "Activate"
              : selectedRow?.status === "active"
                ? "De-activate"
                : "Activate"}
          </ListItemText>
          {menuLoader === "activeInactive" && (
            <ListItemIcon>
              <CircularProgress size={18} sx={{ ml: 1 }} />
            </ListItemIcon>
          )}
        </MenuItem>
        <MenuItem onClick={openTemplateModal} disabled={!!menuLoader}>
          <ListItemText>Save As Template</ListItemText>
        </MenuItem>
        {/* {selectedRow?.status === "draft" && (
          <MenuItem
            onClick={() => handleActionClick("delete")}
            disabled={menuLoader === "delete"}
          >
            <ListItemText sx={{ color: "#F05252" }}>Delete</ListItemText>
            {menuLoader === "delete" && (
              <ListItemIcon>
                <CircularProgress size={18} sx={{ ml: 1 }} />
              </ListItemIcon>
            )}
          </MenuItem>
        )} */}
        {selectedRow?.status === "draft" &&
          selectedRow?.contentType === "builder" && (
            <MenuItem
              disabled={!!menuLoader}
              onClick={() => {
                setAnchorEl(null);
                if (type === "platform") {
                  navigate(
                    `/app/consent/${selectedRow?.id}/edit?versionId=${selectedRow?.versionId}`
                  );
                } else {
                  navigate(
                    `/studies/${studyId}/e-consent/form/${selectedRow?.id}/edit?versionId=${selectedRow?.versionId}&formType=${formType}`
                  );
                }
              }}
            >
              <ListItemText>Open in form editor</ListItemText>
            </MenuItem>
          )}
      </Menu>
      {showModal && (
        <AddEConsentModal
          showModal={showModal}
          closeModal={closeModal}
          data={selectedRow}
          refreshPage={refreshPage}
          previewMode={previewMode}
          type={type}
          formType={formType}
        />
      )}
      {showTemplate && (
        <AddTemplateModal
          showModal={showTemplate}
          closeModal={closeTemplateModal}
          studyId={studyId}
          consentVersionId={selectedRow.versionId}
          consentId={selectedRow.id}
          refreshPage={refreshPage}
          type="econsent"
          consentType={type}
          formType={formType}
        />
      )}
      {showVersionModal && selectedConsent && (
        <EConsentVersionHistoryModal
          showModal={showVersionModal}
          closeModal={closeVersionModal}
          consentName={selectedConsent.consentName}
          consentSummary={selectedConsent.consentSummary}
          studyId={studyId || ""}
          consentId={selectedConsent.consentId}
          type={type}
          formType={formType}
        />
      )}
    </Box>
  );
};

export default EConsentList;
